import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { getStringTag } from 'features/libra/helpers/getStringTag'
import { createLifecycleNotification } from 'features/libra/ticket/createLifecycleNotifications'
import type {
  LibraTicket,
  LibraTicketGroup,
  UpdateTicketArgs,
} from 'features/libra/ticket/libraTicketsApi'
import { useTicketWriter } from 'features/libra/ticket/TicketWriterContext'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { useGeckoPermissions } from 'features/permissions'
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react'
import { createLibraUrl } from 'routes/libra/-searchParams'
import {
  getLibraTicketGroupAdminPermission,
  getLibraTicketGroupEditorPermission,
  getLibraTicketGroupReviewerPermission,
  RolodexEntryType,
  type RolodexPermissionsValues,
} from 'types'

import {
  findTransitionFromStatus,
  resolveActiveStageId,
} from './lifecycleSideEffects'
import type {
  LifecycleConfig,
  LifecycleStage,
  LifecycleTransition,
} from './lifecycleTypes'
import {
  findMatchingTransition,
  getAvailableActions,
  getWorkflowCompleteLabel,
  LifecycleTriggerType,
} from './lifecycleTypes'

// ---------------------------------------------------------------------------
// Available action — a transition the current user can trigger
// ---------------------------------------------------------------------------

export interface LifecycleAction {
  transition: LifecycleTransition
  label: string
  component?: string
  execute: () => Promise<void>
}

export interface LifecycleActionGroup {
  componentKey: string
  actions: LifecycleAction[]
}

const DEFAULT_ACTION_COMPONENT = 'TicketStatusBanner'

const LIBRA_ROLE_BUILDERS: Record<
  string,
  ((id: string) => RolodexPermissionsValues) | undefined
> = {
  'cantilever:libra-ticket-group:admin': getLibraTicketGroupAdminPermission,
  'cantilever:libra-ticket-group:editor': getLibraTicketGroupEditorPermission,
  'cantilever:libra-ticket-group:reviewer':
    getLibraTicketGroupReviewerPermission,
}

export function groupActionsByComponent(
  actions: ReadonlyArray<LifecycleAction>
): LifecycleActionGroup[] {
  const map = new Map<string, LifecycleAction[]>()
  for (const action of actions) {
    const key = action.component ?? DEFAULT_ACTION_COMPONENT
    const group = map.get(key)
    if (group) {
      group.push(action)
    } else {
      map.set(key, [action])
    }
  }
  return Array.from(map.entries()).map(([componentKey, grouped]) => ({
    componentKey,
    actions: grouped,
  }))
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

export interface TicketLifecycleContextType {
  lifecycleConfig: LifecycleConfig | undefined
  currentStage: LifecycleStage | undefined
  availableActions: ReadonlyArray<LifecycleAction>
  actionGroups: ReadonlyArray<LifecycleActionGroup>
  isLocked: boolean
  isTerminal: boolean
  /** True when a `workflow_complete` transition exists from the current stage
   *  (or when no lifecycle config is present, i.e. legacy mode). */
  canCompleteWorkflow: boolean
  /** Fires `workflow_complete` auto-transition (if configured) as a side-effect
   *  of setting workflowComplete=true. Called by TicketAccessContext on last step. */
  completeWorkflow: () => Promise<void>
  /** Label for the last-step "Complete" button, derived from config. */
  workflowCompleteLabel: string
}

const TicketLifecycleContext = createContext<TicketLifecycleContextType | null>(
  null
)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const TicketLifecycleProvider: FC<{
  children: ReactNode
  ticket: LibraTicket
  ticketGroup: LibraTicketGroup | undefined
}> = ({ children, ticket, ticketGroup }) => {
  const workflow = useWorkflow()
  const { user, hasPermissions } = useGeckoPermissions()
  const { ALERTS_URL, BASE_PATH } = useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()
  const { enqueue } = useTicketWriter()

  const lifecycleConfig = workflow.lifecycleConfig
  const groupId = getStringTag(ticket.tags, RolodexEntryType.LIBRA_TICKET_GROUP)

  const scopedHasPermissions = useCallback(
    (perms: RolodexPermissionsValues[]) => {
      if (!groupId) return false
      const scoped = perms.map((p) => {
        const builder = LIBRA_ROLE_BUILDERS[p]
        return builder ? builder(groupId) : p
      })
      return hasPermissions(scoped)
    },
    [hasPermissions, groupId]
  )

  const currentStage = useMemo<LifecycleStage | undefined>(() => {
    if (!lifecycleConfig) return undefined
    const stageId = resolveActiveStageId(lifecycleConfig, ticket.ticketStatus)
    return lifecycleConfig.stages.find((s) => s.id === stageId)
  }, [lifecycleConfig, ticket.ticketStatus])

  const ticketUrl = useMemo(
    () =>
      `${window.location.origin}${BASE_PATH}${createLibraUrl({
        orgId: ticket.tags.organization ?? undefined,
        workflowId: ticket.workflowId,
        ticket: ticket.id,
        thread: undefined,
      })}`,
    [BASE_PATH, ticket.tags.organization, ticket.workflowId, ticket.id]
  )

  const recipientUserIds = useMemo(() => {
    const owners = ticketGroup ? ticketGroup.owners : []
    return [...new Set([...owners, ...ticket.contributors])]
  }, [ticketGroup, ticket.contributors])

  const sendNotification = useCallback(
    (stageName: string) => {
      if (!user) return
      void createLifecycleNotification({
        ticketTitle: ticket.title,
        organizationId: ticket.tags.organization ?? '',
        recipientUserIds,
        actorId: user.id,
        actorName: user.name ?? user.email,
        alertsUrl: ALERTS_URL,
        axios: authorizedAxios,
        ticketUrl,
        stageName,
      })
    },
    [
      user,
      ticket.title,
      ticket.tags.organization,
      recipientUserIds,
      ALERTS_URL,
      authorizedAxios,
      ticketUrl,
    ]
  )

  const availableActions = useMemo<LifecycleAction[]>(() => {
    if (!lifecycleConfig || !currentStage) return []
    const transitions = getAvailableActions(
      lifecycleConfig,
      currentStage.id,
      scopedHasPermissions,
      groupId
    )
    return transitions.map((t) => {
      const trigger = t.trigger as Extract<
        typeof t.trigger,
        { type: LifecycleTriggerType.ACTION }
      >
      const targetStage = lifecycleConfig.stages.find((s) => s.id === t.to)
      return {
        transition: t,
        label: trigger.label,
        component: trigger.component,
        execute: async () => {
          await enqueue(
            () => {
              const updateArgs: Omit<UpdateTicketArgs, 'ticketId'> = {
                ticketStatus: t.to as UpdateTicketArgs['ticketStatus'],
              }
              if (targetStage?.terminal && user) {
                updateArgs.terminalTransitionBy = user.name ?? user.email
                updateArgs.terminalTransitionAt = new Date().toISOString()
              }
              if (!targetStage?.terminal) {
                updateArgs.workflowComplete = false
              }
              return updateArgs
            },
            // Lifecycle action transitions ARE the lifecycle — don't fire
            // FIRST_INTERACTION side effects again.
            { applyInteractionSideEffects: false }
          )
          if (targetStage) {
            sendNotification(targetStage.name)
          }
        },
      }
    })
  }, [
    lifecycleConfig,
    currentStage,
    scopedHasPermissions,
    groupId,
    enqueue,
    sendNotification,
    user,
  ])

  const canCompleteWorkflow = useMemo(() => {
    if (!lifecycleConfig) return true
    if (!currentStage) return false
    return (
      findMatchingTransition(lifecycleConfig, currentStage.id, {
        type: LifecycleTriggerType.WORKFLOW_COMPLETE,
      }) !== null
    )
  }, [lifecycleConfig, currentStage])

  const completeWorkflow = useCallback(async () => {
    if (!lifecycleConfig) {
      await enqueue(() => ({ workflowComplete: true }), {
        applyInteractionSideEffects: false,
      })
      return
    }

    // Resolve the active stage + matching transition INSIDE the enqueue
    // closure against the writer's `latest` snapshot — NOT the render-time
    // `currentStage`. A queued field-save that advanced ticketStatus
    // mid-flow would otherwise leave the WORKFLOW_COMPLETE lookup keyed
    // off the wrong stage. The applied patch flows back out through the
    // enqueue promise (no closure side channel).
    const applied = await enqueue(
      (latest) => {
        const transition = findTransitionFromStatus(
          lifecycleConfig,
          latest.ticketStatus,
          { type: LifecycleTriggerType.WORKFLOW_COMPLETE }
        )
        if (!transition) return null
        const targetStage = lifecycleConfig.stages.find(
          (s) => s.id === transition.to
        )
        const updateArgs: Omit<UpdateTicketArgs, 'ticketId'> = {
          workflowComplete: true,
          ticketStatus: transition.to as UpdateTicketArgs['ticketStatus'],
        }
        if (targetStage?.terminal && user) {
          updateArgs.terminalTransitionBy = user.name ?? user.email
          updateArgs.terminalTransitionAt = new Date().toISOString()
        }
        return updateArgs
      },
      { applyInteractionSideEffects: false }
    )
    // Side effects were disabled above, so `applied.ticketStatus` is
    // exactly the transition target the write landed with.
    const appliedStatus = applied?.ticketStatus
    if (appliedStatus) {
      const targetStage = lifecycleConfig.stages.find(
        (s) => s.id === appliedStatus
      )
      if (targetStage) {
        sendNotification(targetStage.name)
      }
    }
  }, [lifecycleConfig, enqueue, user, sendNotification])

  const workflowCompleteLabel = useMemo(
    () => getWorkflowCompleteLabel(lifecycleConfig, currentStage?.id ?? ''),
    [lifecycleConfig, currentStage?.id]
  )

  const isLocked = currentStage?.locked ?? false
  const isTerminal = currentStage?.terminal ?? false

  const actionGroups = useMemo(
    () => groupActionsByComponent(availableActions),
    [availableActions]
  )

  const value = useMemo<TicketLifecycleContextType>(
    () => ({
      lifecycleConfig,
      currentStage,
      availableActions,
      actionGroups,
      isLocked,
      isTerminal,
      canCompleteWorkflow,
      completeWorkflow,
      workflowCompleteLabel,
    }),
    [
      lifecycleConfig,
      currentStage,
      availableActions,
      actionGroups,
      isLocked,
      isTerminal,
      canCompleteWorkflow,
      completeWorkflow,
      workflowCompleteLabel,
    ]
  )

  return (
    <TicketLifecycleContext.Provider value={value}>
      {children}
    </TicketLifecycleContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useTicketLifecycle = (): TicketLifecycleContextType => {
  const ctx = useContext(TicketLifecycleContext)
  if (!ctx) {
    throw new Error(
      'useTicketLifecycle must be used within a TicketLifecycleProvider'
    )
  }
  return ctx
}
